/**
 * Native palette for the subscription journeys, mapped from the design
 * reference `--p-*` tokens onto the app theme. Tabbio violet is reserved for
 * selection and primary actions; dark mode uses the contrast-safe tint.
 * Native-only: web twins style with `--tabbio-*` CSS variables.
 */
import { useMemo } from 'react';
import { I18nManager } from 'react-native';
import { colors } from '@tabbio/tokens';
import { useTheme } from '@/hooks/useTheme';

export interface JourneyPalette {
  isDark: boolean;
  bg: string;
  /** Grouped-list / secondary-button fill (`--p-group`). */
  group: string;
  text: string;
  secondary: string;
  line: string;
  /** Accent for text, icons and selection borders. */
  brand: string;
  /** Solid primary action fill (white label). */
  brandFill: string;
  /** Selected surface tint (`--p-tint`). */
  tint: string;
  green: string;
  greenFill: string;
  red: string;
  redFill: string;
  amber: string;
  amberFill: string;
}

export function useJourneyPalette(): JourneyPalette {
  const { theme, isDark } = useTheme();
  return useMemo(
    () => ({
      isDark,
      bg: theme.background,
      group: isDark ? '#1c1c1f' : '#f3f3f6',
      text: theme.text,
      secondary: isDark ? '#acacb6' : '#686870',
      line: isDark ? '#3d3d44' : '#e5e5eb',
      brand: isDark ? '#ac93ff' : colors.primary,
      brandFill: isDark ? '#7650f5' : colors.primary,
      tint: isDark ? '#29213d' : '#f0ebff',
      green: isDark ? '#89dab1' : '#187a44',
      greenFill: isDark ? '#173426' : '#eaf6ee',
      red: isDark ? '#ff99a5' : '#bf2539',
      redFill: isDark ? '#3b222b' : '#fff1f2',
      amber: isDark ? '#ffd28c' : '#895100',
      amberFill: isDark ? '#392f21' : '#fff5e6',
    }),
    [isDark, theme.background, theme.text],
  );
}

/**
 * Directional glyphs (chevrons, back arrows) follow the REAL native layout
 * direction, like ScreenHeader / useLeadingTitleHeader / OnboardingFormHeader
 * (issue #82). Rows use plain `row` so Yoga mirrors them under forceRTL; brand
 * marks never flip.
 */
export const flipForRTL = I18nManager.isRTL
  ? ({ transform: [{ scaleX: -1 }] } as const)
  : undefined;

/** Start-aligned text: `left` is swapped to the right edge under native RTL. */
export const TEXT_START = { textAlign: 'left' as const };
